import React from 'react';
import { Modal, Loader, NavigationBackButton, ProviderDialogNavigation, ReviewLayout, ReviewLayoutContent, ReviewLayoutContentControlsProvider, Button } from '@uplift/ui-v2';
import { List } from '@mui/material';
import { useIsActiveMatchBetweenPatientAndProvider, usePatientDocumentsQuery } from '@uplift/queries';
import { PatientDocument, PatientDocumentType } from '@uplift/api';
import { PatientStaticDocumentComponent, documentsMapping } from '../patient-chart-review-modal/components/PatientStaticDocument';
import { CalendarDateService } from '@uplift/services';
import { StaticDocumentSidebarActivityItem, FormSidebarActivityItem, UploadedDocumentSidebarActivityItem } from '../patient-chart-review-modal/components/SidebarActivityList/SidebarActivityItem.component';
import { FormResponsesReviewTable } from '../patient-chart-review-modal';
import { PatientUploadedDocumentReview } from '../patient-chart-review-modal/components/PatientUploadedDocument';
import { useCurrentuser } from '@uplift/contexts';


const DocumentsList: React.FC<{
  patientId: string;
  selectedDocument?: PatientDocument;
  onSelectDocument: (doc: PatientDocument) => void;
}> = ({ patientId, selectedDocument, onSelectDocument }) => {
  const { data } = usePatientDocumentsQuery(patientId);

  React.useEffect(() => {
    if (!data?.documents?.length) {
      return;
    }

    if (selectedDocument) {
      return;
    }

    onSelectDocument(data.documents[0]);
  }, [ data ]);

  return (
    <List disablePadding>
      {data?.documents?.map(doc => {
        if (doc.type === PatientDocumentType.form) {
          return (
            <FormSidebarActivityItem
              key={doc.uploadedAt}
              selected={doc.uploadedAt === selectedDocument?.uploadedAt}
              onSelect={() => onSelectDocument(doc)}
              form={doc.form}
              datetime={doc.uploadedAt}
            />
          );
        }

        if (doc.type === PatientDocumentType.static) {
          return (
            <StaticDocumentSidebarActivityItem
              key={doc.uploadedAt}
              selected={doc.uploadedAt === selectedDocument?.uploadedAt}
              onSelect={() => onSelectDocument(doc)}
              document={doc}
            />
          );
        }

        if (doc.type === PatientDocumentType.medicalRecord) {
          return (
            <UploadedDocumentSidebarActivityItem
              key={doc.uploadedAt}
              selected={doc.uploadedAt === selectedDocument?.uploadedAt}
              onSelect={() => onSelectDocument(doc)}
              document={doc}
            />
          );
        }

        return null;
      })}
    </List>
  );
};


const DocumentPreview: React.FC<{ document: PatientDocument }> = ({ document }) => {
  const me = useCurrentuser();

  switch (document.type) {
    case PatientDocumentType.form:
      return (
        <ReviewLayoutContent
          title={document.form.name}
          subtitle={'sent by ' + document.uploadedBy}
        >
          <FormResponsesReviewTable
            submissionId={document.submission.formSubmissionId}
            patientId={document.submission.userId}
            submittedAt={document.submission.completedAt}
          />
        </ReviewLayoutContent>
      );
    case PatientDocumentType.static:
      return (
        <ReviewLayoutContent
          title={documentsMapping[document.documentKey].title}
          subtitle={'sent by ' + document.uploadedBy}
        >
          <PatientStaticDocumentComponent
            document={document}
            acknowledgedAt={CalendarDateService.date(document.uploadedAt)}
          />
        </ReviewLayoutContent>
      );
    case PatientDocumentType.medicalRecord:
      return (
        <React.Suspense fallback={null}>
          <PatientUploadedDocumentReview
            key={document.id}
            patientId={document.patientId}
            medicalDocumentId={document.id}
            editable={me.id === document.providerId}
            showControls={false}
          />
        </React.Suspense>
      );
  }
};


export type DocumentCenterModalProps = {
  open: boolean;
  onClose: () => void;
  patientId: string;
  selectedDocument?: PatientDocument;
  onSelectDocument: (doc: PatientDocument | null) => void;
  onUploadDocument?: () => void;
};

const DocumentControlsProvider: React.FC<{ patientId: string; selectedItem?: PatientDocument; onSelectItem: (s: PatientDocument) => void }> = (props) => {
  const { data: { documents = [] } = {} } = usePatientDocumentsQuery(props.patientId, undefined);

  return (
    <ReviewLayoutContentControlsProvider
      items={documents}
      findSelectedIndex={(item) => props.selectedItem?.uploadedAt === item.uploadedAt}
      {...props}
    />
  );
};

export const DocumentCenterModal: React.FC<DocumentCenterModalProps> = (props) => {
  const me = useCurrentuser();
  const isActiveMatch = useIsActiveMatchBetweenPatientAndProvider(props.patientId, me.id);

  return (
    <Modal
      open={props.open}
      onClose={props.onClose}
      fullScreen
    >
      <React.Suspense fallback={<Loader />}>
        <ProviderDialogNavigation
          position="sticky"
          title="Document Center"
          leftActions={
            <NavigationBackButton onClick={props.onClose}>Back to Chart</NavigationBackButton>
          }
          rightActions={
            props.onUploadDocument && <Button disabled={!isActiveMatch} variant="contained" color="secondary" onClick={props.onUploadDocument}>Upload document</Button>
          }
        />
        <DocumentControlsProvider
          patientId={props.patientId}
          selectedItem={props.selectedDocument}
          onSelectItem={props.onSelectDocument}
        >
          <ReviewLayout
            sidebarWidth="350px"
            minSidebarWidth="300px"
            sidebar={(
              <DocumentsList
                patientId={props.patientId}
                selectedDocument={props.selectedDocument}
                onSelectDocument={props.onSelectDocument}
              />
            )}
            content={props.selectedDocument && (
              <DocumentPreview
                document={props.selectedDocument}
              />
            )}
          />
        </DocumentControlsProvider>
      </React.Suspense>
    </Modal>
  );
};
